import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Envelope, LockKey, ArrowRight, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

const REMEMBER_KEY = "wahdat_remember_email";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState(() => localStorage.getItem(REMEMBER_KEY) || "");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(() => !!localStorage.getItem(REMEMBER_KEY));
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      if (remember) localStorage.setItem(REMEMBER_KEY, email);
      else localStorage.removeItem(REMEMBER_KEY);
      toast.success("Welcome back");
      const stored = localStorage.getItem("wahdat_user");
      const u = stored ? JSON.parse(stored) : null;
      navigate(u?.role === "parent" ? "/parent" : "/dashboard");
    } else {
      toast.error(res.error || "Login failed");
    }
  }

  function handleForgot(e) {
    e.preventDefault();
    toast.info("Ask your academy admin to reset your password", {
      description: "Coaches can update parent passwords from Settings › Users.",
    });
  }

  if (user) return <Navigate to={user.role === "parent" ? "/parent" : "/dashboard"} replace />;

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-[#050505] px-4 py-10"
      style={{
        backgroundImage:
          "radial-gradient(1200px 500px at 80% -10%, rgba(217,4,41,0.18), transparent 60%), radial-gradient(900px 500px at 0% 100%, rgba(6,214,160,0.10), transparent 60%)",
      }}
    >
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%224%22 height=%224%22><rect width=%224%22 height=%224%22 fill=%22%23ffffff%22 fill-opacity=%220.03%22/></svg>')",
        }}
      />

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="https://customer-assets-39nsmqrw.emergentagent.net/job_ab5c5f51-a164-44df-8a0b-41592b904031/artifacts/ivtkux86_ChatGPT%20Image%20Jul%2023%2C%202026%2C%2008_14_49%20PM.png"
            alt="Wahdat FC Academy"
            className="w-24 h-24 mx-auto object-contain"
          />
          <h1 className="font-display font-black text-3xl uppercase tracking-tight mt-4">Wahdat FC</h1>
          <div className="overline mt-2 text-neutral-500">Academy Management</div>
        </div>

        <form onSubmit={handleSubmit} className="bg-[#0a0a0a] border border-neutral-900 rounded-lg p-6 sm:p-8 space-y-5">
          <div>
            <div className="font-display font-black text-2xl uppercase tracking-tight">Sign in</div>
            <div className="text-sm text-neutral-500 mt-1">Welcome back. Enter your details to continue.</div>
          </div>

          <div>
            <label className="overline block mb-2" htmlFor="login-email-input">Email</label>
            <div className="relative">
              <Envelope size={20} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                id="login-email-input"
                data-testid="login-email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@wahdatfc.com"
                className="w-full h-14 pl-12 pr-4 bg-[#121212] border border-neutral-800 rounded-md text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#D90429]"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="overline" htmlFor="login-password-input">Password</label>
              <button
                type="button"
                onClick={handleForgot}
                data-testid="login-forgot"
                className="text-xs font-bold uppercase tracking-wider text-[#D90429] hover:text-[#ff5c78]"
              >
                Forgot password?
              </button>
            </div>
            <div className="relative">
              <LockKey size={20} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                id="login-password-input"
                data-testid="login-password"
                type={showPw ? "text" : "password"}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full h-14 pl-12 pr-12 bg-[#121212] border border-neutral-800 rounded-md text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#D90429]"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                data-testid="login-toggle-password"
                aria-label={showPw ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-neutral-500 hover:text-white"
              >
                {showPw ? <EyeSlash size={18} weight="bold" /> : <Eye size={18} weight="bold" />}
              </button>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-neutral-300 select-none cursor-pointer">
            <input
              type="checkbox"
              data-testid="login-remember"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="h-4 w-4 accent-[#D90429] rounded"
            />
            Remember me on this device
          </label>

          <button
            type="submit"
            disabled={submitting}
            data-testid="login-submit"
            className="w-full h-14 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white font-display font-bold uppercase tracking-wider text-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            {submitting ? "Signing in…" : (<>Sign In <ArrowRight size={20} weight="bold" /></>)}
          </button>

          <div className="text-center text-xs text-neutral-500 pt-1">
            New family? <a href="/register" className="text-[#D90429] font-bold uppercase tracking-wider" data-testid="login-register-link">Register your child</a>
          </div>
        </form>
      </div>
    </div>
  );
}
